import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../Assets/Styles/Home/Home.css";
import "font-awesome/css/font-awesome.min.css";
import { MEDIA, LANGS, PILLAR_ICONS, WORK_ICONS, INST_ICONS } from "./homeContent";

const VIDEOS = MEDIA.filter((m) => m.type === "video");
const IMAGES = MEDIA.filter((m) => m.type === "image");

export const Home = () => {
    const navigate = useNavigate();
    const [lang, setLang] = useState("en");
    const [speaking, setSpeaking] = useState(false);
    const [voiceNote, setVoiceNote] = useState("");
    const [viewer, setViewer] = useState(null); // null = closed, else { kind: "video" | "image", index }
    const [mediaError, setMediaError] = useState(false);
    const closeRef = useRef(null);
    const audioRef = useRef(null);
    const t = LANGS[lang];

    useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

    // Two sliders: one for all videos, one for all photos (one item at a time)
    const list = viewer ? (viewer.kind === "video" ? VIDEOS : IMAGES) : [];
    const current = viewer ? list[viewer.index] : null;
    const openMedia = (kind) => { setMediaError(false); setViewer({ kind, index: 0 }); };
    const stepMedia = (d) => {
        setMediaError(false);
        setViewer((v) => {
            const n = (v.kind === "video" ? VIDEOS : IMAGES).length;
            return { ...v, index: (v.index + d + n) % n };
        });
    };

    // Viewer keys: Esc closes, arrows move
    useEffect(() => {
        if (!viewer) return;
        closeRef.current && closeRef.current.focus();
        const onKey = (e) => {
            if (e.key === "Escape") setViewer(null);
            if (e.key === "ArrowRight") stepMedia(1);
            if (e.key === "ArrowLeft") stepMedia(-1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [!!viewer]); // eslint-disable-line react-hooks/exhaustive-deps

    // Plays our own recorded voice (mp3) for the selected language
    const stopVoice = () => {
        if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
        setSpeaking(false);
    };
    const changeLang = (code) => { stopVoice(); setVoiceNote(""); setLang(code); };

    const toggleVoice = () => {
        if (speaking) { stopVoice(); return; }
        setVoiceNote("");
        const audio = new Audio(t.audio);
        audioRef.current = audio;
        audio.onended = () => setSpeaking(false);
        audio.onerror = () => { setSpeaking(false); console.error("Voice file failed to load:", t.audio); setVoiceNote(`Voice file could not be loaded: ${t.audio}`); };
        audio.play().then(() => setSpeaking(true)).catch((err) => { setSpeaking(false); console.error("Voice play failed:", err); setVoiceNote(`Could not play the voice file: ${t.audio}`); });
    };

    return (
        <>
            <main className="hm-page" lang={lang}>
                <section className="hm-intro">
                    <div className="hm-top">
                        <div className="hm-langs" role="group" aria-label="Language">
                            {Object.entries(LANGS).map(([code, l]) => (
                                <button key={code} type="button" className={code === lang ? "active" : ""}
                                    aria-pressed={code === lang} onClick={() => changeLang(code)}>{l.label}</button>
                            ))}
                        </div>
                        <button type="button" className="hm-btn hm-btn-line hm-listen" onClick={toggleVoice} aria-pressed={speaking}>
                            <i className={`fa ${speaking ? "fa-stop-circle" : "fa-volume-up"}`} aria-hidden="true" /> {speaking ? t.stop : t.listen}
                        </button>
                    </div>

                    <h1>{t.headline}</h1>
                    <p className="hm-sub">{t.sub}</p>

                    <div className="hm-actions">
                        <button type="button" className="hm-btn hm-btn-solid" onClick={() => navigate("/robot-operation?pid=1")}>
                            <i className="fa fa-cogs" aria-hidden="true" /> {t.operate}
                        </button>
                        <button type="button" className="hm-btn hm-btn-line" onClick={() => openMedia("video")}>
                            <i className="fa fa-play-circle" aria-hidden="true" /> {t.watch}
                        </button>
                    </div>
                    {voiceNote && <p className="hm-note" role="status">{voiceNote}</p>}

                    <div className="hm-media">
                        <span className="hm-media-label">{t.gallery}</span>
                        <ul>
                            {VIDEOS.length > 0 && (
                                <li>
                                    <button type="button" onClick={() => openMedia("video")} aria-label={`Videos (${VIDEOS.length})`}>
                                        <span className="hm-vthumb"><i className="fa fa-play" aria-hidden="true" /></span>
                                        {VIDEOS.length > 1 && <span className="hm-vbadge">{VIDEOS.length}</span>}
                                    </button>
                                </li>
                            )}
                            {IMAGES.length > 0 && (
                                <li>
                                    <button type="button" onClick={() => openMedia("image")} aria-label={`Photos (${IMAGES.length})`}>
                                        <img src={IMAGES[0].src} alt="" onError={() => console.error("Photo failed to load:", IMAGES[0].src)} />
                                        {IMAGES.length > 1 && <span className="hm-vbadge">{IMAGES.length}</span>}
                                    </button>
                                </li>
                            )}
                        </ul>
                    </div>
                </section>

                <aside className="hm-side">
                    <h2>{t.pillarsTitle}</h2>
                    <ul>
                        {t.pillars.map((p, i) => (
                            <li key={i}>
                                <i className={`fa ${PILLAR_ICONS[i]}`} aria-hidden="true" />
                                <div><h3>{p[0]}</h3><p>{p[1]}</p></div>
                            </li>
                        ))}
                    </ul>
                </aside>
            </main>

            <section className="hm-works" lang={lang}>
                <h2>{t.worksTitle}</h2>
                <ul className="hm-works-grid">
                    {t.works.map((w, i) => (
                        <li key={i}>
                            <span className="hm-ico"><i className={`fa ${WORK_ICONS[i]}`} aria-hidden="true" /></span>
                            <h3>{w[0]}</h3>
                            <p>{w[1]}</p>
                        </li>
                    ))}
                </ul>
            </section>

            <section className="hm-farmers" lang={lang}>
                <div className="hm-farmers-inner">
                    <div className="hm-farmers-lead">
                        <h2>{t.instTitle}</h2>
                        <p>{t.farmersText}</p>
                    </div>
                    <ul className="hm-farmers-grid">
                        {t.inst.map((w, i) => (
                            <li key={i}>
                                <span className="hm-num" aria-hidden="true">0{i + 1}</span>
                                <span className="hm-ico"><i className={`fa ${INST_ICONS[i]}`} aria-hidden="true" /></span>
                                <h3>{w[0]}</h3>
                                <p>{w[1]}</p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="hm-cta-wrap" lang={lang}>
                <div className="hm-cta">
                    <div><h2>{t.ctaTitle}</h2><p>{t.ctaText}</p></div>
                    {/*<button type="button" className="hm-btn hm-btn-ink" onClick={() => navigate("/robot-operation?pid=1")}>*/}
                    {/*    <i className="fa fa-sign-in" aria-hidden="true" /> {t.operate}*/}
                    {/*</button>*/}
                </div>
            </section>

            {current && (
                <div className="hm-modal" onClick={() => setViewer(null)}>
                    <div className="hm-modal-box" role="dialog" aria-modal="true" aria-label={current.title} onClick={(e) => e.stopPropagation()}>
                        <button ref={closeRef} type="button" className="hm-close" onClick={() => setViewer(null)} aria-label="Close">
                            <i className="fa fa-times" />
                        </button>
                        {current.type === "video" ? (
                            mediaError ? (
                                <p className="hm-video-missing">Video not found. Add the file at <code>public{current.src}</code>.</p>
                            ) : (
                                <video key={current.src} src={current.src} controls autoPlay playsInline onError={() => setMediaError(true)} />
                            )
                        ) : (
                            <img key={current.src} src={current.src} alt={current.title} />
                        )}
                        <div className="hm-caption">
                            {list.length > 1 && <button type="button" onClick={() => stepMedia(-1)} aria-label="Previous"><i className="fa fa-chevron-left" /></button>}
                            <span>{current.title}{list.length > 1 ? ` (${viewer.index + 1}/${list.length})` : ""}</span>
                            {list.length > 1 && <button type="button" onClick={() => stepMedia(1)} aria-label="Next"><i className="fa fa-chevron-right" /></button>}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Home;